import type { StopPreview } from '../../shared/types.js';

/**
 * The little map on a tour card.
 *
 * **Why there is no image anywhere in this.** The obvious way to put a picture of a track on
 * a card is to render one on the server, and the obvious way to do that is a headless
 * browser. On the box this is going to run on — one vCPU — Chromium would want more memory
 * for a thumbnail than the game uses for a city, and it would want it once per tour. So the
 * card is drawn from numbers instead: the road centrelines the region picker had already
 * fetched, stored with the tour and turned into an SVG path here, in the browser that is
 * looking at it. The server does nothing at all, and a tour costs a few kilobytes rather
 * than a JPEG.
 *
 * It also survives being resized, restyled and put on a dark background, which a raster
 * screenshot of a night-time city very much does not.
 */

/** Roughly metres per degree of latitude. Good to a fraction of a percent, which is a lot
 * better than a two-hundred-pixel card can show. */
const METRES_PER_DEGREE = 111_320;

/**
 * Turns the `/api/preview` payload into what gets stored with the tour.
 *
 * Latitude and longitude are converted to a local flat frame because the card wants a
 * picture, not a position: an equirectangular projection with the cosine correction at this
 * latitude is exact enough over the couple of kilometres a region spans, and it means the
 * card never has to know where on Earth it is.
 */
export function buildStopPreview(lines: number[][], route: number[]): StopPreview | null {
  const all = route.length >= 4 ? [...lines, route] : lines;
  if (!all.length) return null;

  let latSum = 0;
  let lonSum = 0;
  let points = 0;
  for (const line of all) {
    for (let i = 0; i < line.length; i += 2) {
      latSum += line[i];
      lonSum += line[i + 1];
      points++;
    }
  }
  if (!points) return null;
  const lat0 = latSum / points;
  const lon0 = lonSum / points;
  const lonScale = Math.cos((lat0 * Math.PI) / 180) * METRES_PER_DEGREE;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const project = (line: number[]): number[] => {
    const out: number[] = new Array(line.length);
    for (let i = 0; i < line.length; i += 2) {
      // Screen convention: y grows downwards, so north is up without a transform later.
      const x = (line[i + 1] - lon0) * lonScale;
      const y = -(line[i] - lat0) * METRES_PER_DEGREE;
      out[i] = x;
      out[i + 1] = y;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    return out;
  };

  const projected = thin(lines.map(project).filter((line) => line.length >= 4));
  const projectedRoute = route.length >= 4 ? project(route) : [];
  if (!projected.length && projectedRoute.length < 4) return null;

  return {
    lines: projected.map(round),
    route: round(projectedRoute),
    box: [Math.round(minX), Math.round(minY), Math.round(maxX - minX), Math.round(maxY - minY)],
  };
}

/**
 * The most roads a card carries.
 *
 * Kraków's Old Town comes back with 453 of them, which the server's own bound rejects — so
 * the first version of this stored no sketch at all for exactly the regions most worth
 * looking at, and did it silently, because a missing thumbnail is not an error. The cap
 * lives here rather than only there so that the trimming is a deliberate choice about what
 * a two-hundred-pixel picture can show, made where the picture is drawn.
 */
const MAX_LINES = 200;

/**
 * Keeps the longest roads.
 *
 * Length is a decent proxy for importance at this scale — a through road is long, a
 * cul-de-sac behind a block is not — and `buildPreviewLines` has already dropped anything
 * under seven metres wide, so what is left to choose between is all real street.
 */
function thin(lines: number[][]): number[][] {
  if (lines.length <= MAX_LINES) return lines;
  return [...lines].sort((a, b) => span(b) - span(a)).slice(0, MAX_LINES);
}

/** Bounding-box diagonal, squared. Cheaper than a polyline length and ranks the same. */
function span(line: number[]): number {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i < line.length; i += 2) {
    if (line[i] < minX) minX = line[i];
    if (line[i] > maxX) maxX = line[i];
    if (line[i + 1] < minY) minY = line[i + 1];
    if (line[i + 1] > maxY) maxY = line[i + 1];
  }
  return (maxX - minX) ** 2 + (maxY - minY) ** 2;
}

/**
 * Whole metres.
 *
 * A card is about two hundred pixels across a region a couple of kilometres wide, so one
 * pixel is roughly ten metres and a decimal place is two orders of magnitude more precision
 * than anything can show. It is not free, either: every digit is a byte in every copy of
 * every tour, and Kraków's Old Town alone is two hundred road lines.
 */
function round(line: number[]): number[] {
  return line.map(Math.round);
}

/**
 * Draws one, sized by CSS rather than by attributes so the same preview serves a card and a
 * larger panel.
 */
export function previewSvg(preview: StopPreview | undefined, className = 'tour-preview'): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', className);
  svg.setAttribute('aria-hidden', 'true');
  if (!preview) {
    // No sketch — an older tour, or one whose regions never previewed. The card still needs
    // something the right shape, so it gets an empty frame rather than a collapsed box.
    svg.setAttribute('viewBox', '0 0 100 60');
    return svg;
  }

  const [x, y, width, height] = preview.box;
  // A hair of margin, so a road running along the edge is not clipped to a hairline.
  const pad = Math.max(width, height) * 0.04 + 2;
  svg.setAttribute('viewBox', `${x - pad} ${y - pad} ${width + pad * 2} ${height + pad * 2}`);
  // `meet`, not `slice`. Filling the card looks better in the abstract, but a region is
  // rarely 16:9 and cropping to fit cuts the ends off the racing line — which is the one
  // thing on the card somebody is actually trying to read.
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  const roads = pathData(preview.lines);
  if (roads) {
    const node = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    node.setAttribute('d', roads);
    node.setAttribute('class', 'tour-preview-roads');
    // Stroke widths are in the same metres as the geometry, so they scale with the region
    // rather than with the card — a 2 km box and a 500 m box both read as a street map.
    node.setAttribute('stroke-width', String(Math.max(width, height) * 0.006 + 1));
    svg.append(node);
  }

  if (preview.route.length >= 4) {
    const node = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    node.setAttribute('d', pathData([preview.route]));
    node.setAttribute('class', 'tour-preview-route');
    node.setAttribute('stroke-width', String(Math.max(width, height) * 0.012 + 2));
    svg.append(node);
  }
  return svg;
}

/** One `d` for every line, which is one node instead of nine hundred. */
function pathData(lines: number[][]): string {
  const parts: string[] = [];
  for (const line of lines) {
    if (line.length < 4) continue;
    let d = `M${line[0]} ${line[1]}`;
    for (let i = 2; i < line.length; i += 2) d += `L${line[i]} ${line[i + 1]}`;
    parts.push(d);
  }
  return parts.join('');
}
